// Source-space predicate-AST utilities shared by every virtual-view builder.
//
// A virtual view presents columns under canonical (de-prefixed/renamed) names
// but reads from a source CDF view under the source names. A predicate written
// against the canonical surface is identically a predicate against the source
// surface with the column names swapped — `renameCanonicalToSource` performs
// that lossless inversion so a facade can fold the agent's filter into its
// inner source query, and the leaf hooks can push source-native predicates.
// `andFilters` / `andClauses` fold the fixed `where`, the pushed source-native
// remainder, and resolved ancestor-scope clauses into one source filter.

import { type AstInput, astShape } from '#core/lib/expr/normalize.ts';
import { rowColLabels } from '#core/lib/expr/providers/decode.ts';

import { kw, op, ROW } from './ast.ts';
import { isResolvedStruct, type Resolved, type ResolvedFlat, type ResolvedStruct } from './shapes/index.ts';

/**
 * Rewrite `(col % <canonical> …)` → `(col % <source> …)` in a row-column ref's
 * first label, throughout a predicate tree. Relation names and identity are
 * left untouched (no `cols` entry rewrites them). Walks the whole node so
 * nested predicate trees rename too.
 */
export const renameCanonicalToSource = (node: AstInput, cols: readonly Resolved[]): AstInput => {
  // A flat column swaps its single canonical name back to its source name. A
  // struct column has no server-side column of its own, but a sub-path filter
  // `(col % <canonical> <member>)` names a real member source column, so it
  // rewrites to that source column directly.
  const bySource = new Map(
    cols
      .filter((c: Resolved): c is ResolvedFlat => !isResolvedStruct(c))
      .map((c: ResolvedFlat) => [c.canonical, c.source] as const),
  );
  const byStruct = new Map(
    cols
      .filter((c: Resolved): c is ResolvedStruct => isResolvedStruct(c))
      .map((c: ResolvedStruct) => [c.canonical, c] as const),
  );
  const shape = astShape(node);
  if (shape.kind !== 'opCall') {
    return node;
  }
  const { name } = shape.call;
  const params = (shape.call.params ?? []) as readonly AstInput[];

  if (name === 'col') {
    const labels = rowColLabels(node) ?? [];
    const first = labels[0];
    const flat = first === undefined ? undefined : bySource.get(first);
    if (flat !== undefined && flat !== first) {
      const rest = labels.slice(1).map((l: string) => kw(l));
      return op('col', ROW, kw(flat), ...rest);
    }
    const struct = first === undefined ? undefined : byStruct.get(first);
    const memberSource = struct === undefined ? undefined : struct.members[labels[1] ?? ''];
    if (memberSource !== undefined) {
      return op('col', ROW, kw(memberSource), ...labels.slice(2).map((l: string) => kw(l)));
    }
    return node;
  }

  return { name, params: params.map((p: AstInput) => renameCanonicalToSource(p, cols)) };
};

/** Re-AND a list of clauses: `[]` → undefined, `[x]` → x, else `(and …)`. */
export const andClauses = (clauses: readonly AstInput[]): AstInput | undefined => {
  if (clauses.length === 0) {
    return undefined;
  }
  return clauses.length === 1 ? (clauses[0] as AstInput) : op('and', ...clauses);
};

/**
 * Combine two optional source-space predicates with `and`: both absent →
 * undefined, one absent → the other, both present → `(and a b)`. The SSOT for
 * folding the fixed `where`, the pushed source-native remainder, and the
 * resolved ancestor-scope clause into one source query filter.
 */
export const andFilters = (a: AstInput | undefined, b: AstInput | undefined): AstInput | undefined =>
  andClauses([a, b].flatMap((c: AstInput | undefined) => (c === undefined ? [] : [c])));
